import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { KanbanFieldMapping } from '@/types/pm-types';
import { KanbanCard, getCardId } from './KanbanCard';
import { pmTheme } from './pmTheme';

interface ColumnDef {
  id: string;
  label: string;
  color?: string;
  wipLimit?: number;
}

export interface KanbanColumnProps {
  columnDef: ColumnDef;
  cards: Record<string, unknown>[];
  fieldMapping: KanbanFieldMapping;
  cardLayout: 'compact' | 'standard' | 'detailed';
  colorMapping?: Record<string, string>;
  showColumnCounts: boolean;
  showWipLimits?: boolean;
  maxVisibleCards?: number;
  isViewerMode: boolean;
  dragEnabled: boolean;
  onCardClick?: (record: Record<string, unknown>) => void;
  onEditItem?: (itemId: string, itemData: Record<string, unknown>) => void;
}

export const KanbanColumn: React.FC<KanbanColumnProps> = ({
  columnDef,
  cards,
  fieldMapping,
  cardLayout,
  colorMapping,
  showColumnCounts,
  showWipLimits,
  isViewerMode,
  maxVisibleCards,
  dragEnabled,
  onCardClick,
  onEditItem,
}) => {
  const { setNodeRef } = useDroppable({
    id: `column-${columnDef.id}`,
    disabled: !isViewerMode || !dragEnabled,
    data: { type: 'column', columnId: columnDef.id },
  });

  const visibleCards = maxVisibleCards != null ? cards.slice(0, maxVisibleCards) : cards;
  const hiddenCount = cards.length - visibleCards.length;
  const cardIds = visibleCards.map((record, i) => getCardId(record, fieldMapping, i));
  const isOverWipLimit = columnDef.wipLimit != null && cards.length > columnDef.wipLimit;

  return (
    <div
      data-testid={`kanban-column-${columnDef.id}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 0%',
        minWidth: 200,
        background: pmTheme.surfaceSubtle,
        borderRadius: 8,
        padding: 8,
        overflow: 'hidden',
      }}
    >
      {/* Column header */}
      <div
        data-testid={`kanban-column-header-${columnDef.id}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 4px 8px',
          borderBottom: '2px solid',
          borderColor: columnDef.color || pmTheme.input,
          marginBottom: 8,
        }}
      >
        <span style={{ fontWeight: 600, fontSize: 13, color: pmTheme.text }}>
          {columnDef.label}
        </span>
        {showColumnCounts && (
          <span
            data-testid={`kanban-column-count-${columnDef.id}`}
            style={{
              fontSize: 11,
              fontWeight: 500,
              padding: '1px 6px',
              borderRadius: 10,
              background: isOverWipLimit ? pmTheme.dangerSoft : pmTheme.surfaceMuted,
              color: isOverWipLimit ? pmTheme.danger : pmTheme.textMuted,
            }}
          >
            {cards.length}
          </span>
        )}
        {showWipLimits && columnDef.wipLimit != null && (
          <span
            data-testid={`kanban-column-wip-${columnDef.id}`}
            style={{
              fontSize: 10,
              color: isOverWipLimit ? pmTheme.danger : pmTheme.textSubtle,
              fontWeight: isOverWipLimit ? 600 : 400,
              marginLeft: 'auto',
            }}
          >
            WIP: {cards.length}/{columnDef.wipLimit}
          </span>
        )}
      </div>

      {/* Card list */}
      <SortableContext items={cardIds} strategy={verticalListSortingStrategy}>
        <div
          ref={setNodeRef}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            flex: 1,
            overflowY: 'auto',
            minHeight: 60,
            padding: 2,
          }}
        >
          {cards.length === 0 ? (
            <div
              data-testid={`kanban-column-empty-${columnDef.id}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 60,
                border: `2px dashed ${pmTheme.border}`,
                borderRadius: 6,
                color: pmTheme.textSubtle,
                fontSize: 12,
              }}
            >
              No items
            </div>
          ) : (
            <>
              {visibleCards.map((record, i) => (
                <KanbanCard
                  key={getCardId(record, fieldMapping, i)}
                  record={record}
                  fieldMapping={fieldMapping}
                  cardLayout={cardLayout}
                  colorMapping={colorMapping}
                  isViewerMode={isViewerMode}
                  dragEnabled={dragEnabled}
                  index={i}
                  onClick={onCardClick}
                  onEditClick={onEditItem}
                />
              ))}
              {hiddenCount > 0 && (
                <div
                  style={{
                    textAlign: 'center',
                    fontSize: 11,
                    color: pmTheme.textMuted,
                    padding: '4px 0',
                  }}
                >
                  +{hiddenCount} more
                </div>
              )}
            </>
          )}
        </div>
      </SortableContext>
    </div>
  );
};

KanbanColumn.displayName = 'KanbanColumn';
